import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { useNavigate } from '@tanstack/react-router'
import { loginRequestSchema, type LoginRequest } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { TextField } from '../components/ui/field.js'
import { Logo } from '../components/logo.js'
import { Alert } from '../components/ui/feedback.js'
import { t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

export function LoginPage() {
  const strings = t()
  const { signIn, user } = useAuth()
  const navigate = useNavigate()
  const [formError, setFormError] = useState<string | null>(null)

  // Covers both arriving here already signed in — a bookmark, a second tab —
  // and the moment the sign-in below succeeds.
  useEffect(() => {
    if (user) void navigate({ to: '/status' })
  }, [user, navigate])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: standardSchemaResolver(loginRequestSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      await signIn(values.email, values.password)
    } catch (error) {
      if (error instanceof ApiError) {
        // 429 gets its own wording: "wrong password" would be actively
        // misleading when the credentials were never checked.
        setFormError(
          error.status === 429
            ? strings.login.rateLimited
            : (error.problem.detail ?? strings.login.failed),
        )
        return
      }
      setFormError(strings.login.networkError)
    }
  })

  return (
    <main className="bg-surface-sunken flex min-h-dvh items-center justify-center p-4">
      <div className="bg-surface-raised border-border w-full max-w-sm rounded-lg border p-6 shadow-sm">
        <h1 className="sr-only">{strings.app.title}</h1>
        <Logo className="text-xl" withDomain />
        <p className="text-text-muted mt-4 text-sm">{strings.login.intro}</p>

        <form
          onSubmit={(event) => void onSubmit(event)}
          // The browser's own constraint validation would swallow the submit
          // before React ever sees it, and replace our German messages with
          // whatever locale the browser happens to run in.
          noValidate
          className="mt-6 flex flex-col gap-4"
        >
          {formError && <Alert tone="danger">{formError}</Alert>}

          <TextField
            label={strings.login.email}
            type="email"
            autoComplete="username"
            autoFocus
            error={errors.email?.message}
            {...register('email')}
          />

          <TextField
            label={strings.login.password}
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          <Button type="submit" disabled={isSubmitting} className="mt-2">
            {isSubmitting ? strings.login.submitting : strings.login.submit}
          </Button>
        </form>
      </div>
    </main>
  )
}
